import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { ShoppingCart, User, LogOut, LogIn, Bike, Shield, ClipboardList, Settings } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { useAuthStore } from '@/lib/auth';
import { useCart } from '@/lib/cart';
import { Logo } from '@/components/Logo';
import { OpenClosedBadge } from '@/lib/schedule';

export const PublicHeader = () => {
  const nav = useNavigate();
  const { user, logout } = useAuthStore();
  const totalItems = useCart(s => s.totalItems());

  return (
    <header className="sticky top-0 z-40 backdrop-blur bg-[#F7F5EF]/90 border-b border-[--brand-border]" style={{ paddingTop: 'env(safe-area-inset-top)' }}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
        <Link to="/" className="flex items-center gap-2" data-testid="header-logo-link">
          <Logo variant="compact" />
        </Link>

        <div className="hidden md:block"><OpenClosedBadge /></div>

        <nav className="hidden md:flex items-center gap-2">
          <Link to="/menu" className="px-3 py-2 rounded-lg text-sm hover:bg-[--brand-surface-2]" data-testid="header-menu-link">Menu</Link>
          <Link to="/builder" className="px-3 py-2 rounded-lg text-sm hover:bg-[--brand-surface-2]" data-testid="header-builder-link">Crea la tua Poke</Link>
          {user?.role === 'customer' && (
            <Link to="/my-orders" className="px-3 py-2 rounded-lg text-sm hover:bg-[--brand-surface-2]" data-testid="header-my-orders-link">I miei ordini</Link>
          )}
        </nav>

        <div className="flex items-center gap-2">
          <Link to="/cart" className="relative" data-testid="header-cart-link">
            <Button variant="ghost" size="icon" className="rounded-xl">
              <ShoppingCart size={20} />
            </Button>
            {totalItems > 0 && (
              <Badge className="absolute -top-1 -right-1 h-5 min-w-5 rounded-full px-1.5 bg-[--brand-accent] text-[--brand-text] border-transparent" data-testid="header-cart-count">
                {totalItems}
              </Badge>
            )}
          </Link>
          {user ? (
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="rounded-xl" data-testid="header-user-button">
                  <User size={20} />
                </Button>
              </SheetTrigger>
              <SheetContent>
                <SheetHeader><SheetTitle>Ciao, {user.name}</SheetTitle></SheetHeader>
                <div className="mt-4 space-y-2 text-sm text-[--brand-muted]">
                  <div>{user.email}</div>
                  <div>Ruolo: <span className="font-semibold text-[--brand-text]">{user.role}</span></div>
                </div>
                <div className="mt-6 space-y-2">
                  {user.role === 'customer' && (
                    <>
                      <Button variant="secondary" className="w-full rounded-xl" onClick={() => nav('/my-orders')} data-testid="header-menu-orders"><ClipboardList size={16} className="mr-2"/>I miei ordini</Button>
                      <Button variant="secondary" className="w-full rounded-xl" onClick={() => nav('/profile')} data-testid="header-menu-profile"><Settings size={16} className="mr-2"/>Il mio profilo</Button>
                    </>
                  )}
                  {user.role === 'admin' && (
                    <Button variant="secondary" className="w-full rounded-xl" onClick={() => nav('/admin')}><Shield size={16} className="mr-2"/>Dashboard admin</Button>
                  )}
                  {user.role === 'rider' && (
                    <Button variant="secondary" className="w-full rounded-xl" onClick={() => nav('/rider')}><Bike size={16} className="mr-2"/>App rider</Button>
                  )}
                  <Button variant="destructive" className="w-full rounded-xl" onClick={() => { logout(); nav('/'); }} data-testid="header-logout-button"><LogOut size={16} className="mr-2"/>Esci</Button>
                </div>
              </SheetContent>
            </Sheet>
          ) : (
            <Link to="/login" data-testid="header-login-link">
              <Button variant="secondary" className="rounded-xl hidden sm:inline-flex">
                <LogIn size={16} className="mr-2"/>Accedi
              </Button>
              <Button variant="ghost" size="icon" className="rounded-xl sm:hidden">
                <LogIn size={20} />
              </Button>
            </Link>
          )}
        </div>
      </div>
      <div className="md:hidden max-w-6xl mx-auto px-4 pb-2 flex flex-col gap-1.5">
        <nav className="flex gap-1 text-xs items-center whitespace-nowrap">
          <Link to="/menu" className="px-2 py-1 rounded-md hover:bg-[--brand-surface-2]">Menu</Link>
          <Link to="/builder" className="px-2 py-1 rounded-md hover:bg-[--brand-surface-2]">Componi</Link>
          {user?.role === 'customer' && (
            <Link to="/my-orders" className="px-2 py-1 rounded-md hover:bg-[--brand-surface-2]">Ordini</Link>
          )}
        </nav>
        <div className="w-full"><OpenClosedBadge compact /></div>
      </div>
    </header>
  );
};

export const Footer = () => {
  return (
  <footer className="mt-16 border-t border-[--brand-border] bg-white">
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 grid gap-6 sm:grid-cols-3 text-sm">
      <div>
        <Logo variant="compact" />
        <p className="text-[--brand-muted] mt-2">Ingredienti freschi, ricette autentiche, consegna veloce.</p>
        <div className="flex items-center gap-2 mt-3">
          <a href="https://www.instagram.com/moma_poke" target="_blank" rel="noopener noreferrer"
             className="w-9 h-9 rounded-full bg-[--brand-primary] text-white flex items-center justify-center hover:bg-[--brand-primary-hover] transition-colors"
             aria-label="Instagram MōMA Pokè" data-testid="footer-instagram-link">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/></svg>
          </a>
          <a href="https://www.tiktok.com/@moma_poke" target="_blank" rel="noopener noreferrer"
             className="w-9 h-9 rounded-full bg-[--brand-primary] text-white flex items-center justify-center hover:bg-[--brand-primary-hover] transition-colors"
             aria-label="TikTok MōMA Pokè" data-testid="footer-tiktok-link">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.53a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.84-.36z"/></svg>
          </a>
        </div>
      </div>
      <div>
        <div className="font-semibold mb-2">Link</div>
        <ul className="space-y-1 text-[--brand-muted]">
          <li><Link to="/menu">Menu</Link></li>
          <li><Link to="/builder">Crea la tua Poke</Link></li>
          <li><Link to="/admin/login">Accesso admin</Link></li>
          <li><Link to="/rider/login">Accesso rider</Link></li>
        </ul>
      </div>
      <div>
        <div className="font-semibold mb-2">Contatti</div>
        <p className="text-[--brand-muted]">Corso delle Province 169A<br/>95127 Catania</p>
        <p className="text-[--brand-muted] mt-2">Pagamento contanti o carta alla consegna</p>
      </div>
    </div>
    <div className="text-center text-xs text-[--brand-muted] pb-4">© {new Date().getFullYear()} MōMA Pokè · Tutti i diritti riservati</div>
  </footer>
  );
};
